import React, { useCallback, useEffect, useState } from 'react';
import {
  withWidth,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  useTheme
} from '@material-ui/core';
import * as api from '../../services/apiService';
import ClearIcon from '@material-ui/icons/Clear';
import emptySaerch from '../../assets/emptySearch.png';
import emptySaerchDark from '../../assets/dark/searchDark.png';
import { searchUsersStyle } from './Styles';

interface SearchUsersPropsType {
  title: string;
  selectedUsers: any;
  setSelectedUsers: (users: any) => void;
  closeDialog: (close: boolean) => void;
}

const SearchUsers: React.FC<SearchUsersPropsType> = (props) => {
  const classes = searchUsersStyle();
  const {
    title,
    selectedUsers,
    setSelectedUsers,
    closeDialog
  } = props;
  const isDarkTheme = useTheme().palette.type === 'dark';
  const [searchValue, setSearchValue] = React.useState<string>('');
  const [items, setItems] = React.useState<string[]>([]);
  const [performSearch, setPerformSearch] = React.useState<boolean>(false);
  const [localSelectedUsers, setLocalSelectedUsers] = useState<any[]>([]);
  const [searchValueSelected, setSearchValueSelected] = useState<boolean>(false);

  const doSearch = useCallback(async () => {
    const { data, status_code: statusCode } = await api.searchUsers(searchValue);
    if (statusCode === api.status.SUCCESS) {
      const updatedData = data.map(user => {
        const previouslySelected =
          selectedUsers.find(sel => sel.id === user.id && sel.selected) ||
          localSelectedUsers.find(sel => sel.id === user.id && sel.selected);

        return {
          ...user,
          selected: previouslySelected ? true : false,
        };
      });

      setItems(updatedData);
      setPerformSearch(true);
    }
  }, [searchValue, selectedUsers, localSelectedUsers]);


  useEffect(() => {
    if (searchValue === '') {
      setItems([]);
    } else {
      doSearch();
    }
  }, [doSearch, searchValue]);

  useEffect(() => {
    if (selectedUsers.length > 0) {
      setItems(selectedUsers);
    }
  }, [])

  const onSave = () => {
    const searchSelectedUsers = items.filter((user: any) => user.selected);

    const manualSelected = (searchValue && searchValueSelected)
      ? [{
        invitee_name: searchValue,
        selected: true,
      }]
      : [];

    const remainingFromSelectedUsers = selectedUsers.filter(prev =>
      (prev.id && !items.some((item: any) => item.id === prev.id && !item.selected)) ||
      (!prev.id && !items.some((item: any) => item.invitee_name === prev.invitee_name && !item.selected))
    );


    const merged = [
      ...remainingFromSelectedUsers,
      ...searchSelectedUsers,
      ...manualSelected
    ].filter((user, index, self) =>
      index === self.findIndex(
        u => u.id ? u.id === user.id : u.invitee_name === user.invitee_name
      )
    )

    setSelectedUsers(merged);
    closeDialog(false);
  };

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
  };

  const clearSearch = () => {
    setSearchValue('');
    setItems([...selectedUsers]);
  };

  const inputProps = {
    endAdornment: (
      <InputAdornment position="end">
        <ClearIcon onClick={clearSearch} className={classes.cursor}></ClearIcon>
      </InputAdornment>
    )
  };

  const selectUser = (user) => {
    setItems(prevItems =>
      prevItems.map((item: any) => {
        const isSameUser = item.id
          ? item.id === user.id
          : item.invitee_name === user.invitee_name;

        return isSameUser ? { ...item, selected: !item.selected } : item;
      })
    );
  };

  return (
    <div className={classes.container}>
      <ClearIcon className={classes.icon} onClick={() => closeDialog(false)} />
      <Typography className={classes.header} variant="h1">
        {title}
      </Typography>
      <Box mb={1} className={classes.searchBox}>
        <TextField
          InputProps={searchValue.length > 0 ? inputProps : undefined}
          variant="outlined"
          label="Search"
          margin="normal"
          fullWidth
          value={searchValue}
          onChange={(event) => handleSearchChange(event)}
        />
      </Box>
      <Paper
        elevation={0}
        className={
          items.length > 0 ? classes.resultBoxWithBorder : classes.resultBox
        }
      >
        <>
          {searchValue.trim() && <Box>
            <Box className={classes.searchBox}>
              <FormControlLabel
                control={
                  <Checkbox
                    classes={{
                      root: classes.root,
                      checked: classes.checked
                    }}
                    checked={searchValueSelected}
                    className={classes.checkBoxColor}
                    onClick={() => setSearchValueSelected(!searchValueSelected)}
                  />
                }
                label={
                  <div style={{ paddingTop: 10, paddingBottom: 10 }}>
                    <Typography variant="h6"> {searchValue}</Typography>
                  </div>
                }
              />
            </Box>
            <Divider></Divider>
          </Box>}
          {items.length > 0 ? (
            items.map((item: any) => (
              <Box key={item.id}>
                <Box className={classes.searchBox}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        classes={{
                          root: classes.root,
                          checked: classes.checked
                        }}
                        checked={item.selected || false}
                        className={classes.checkBoxColor}
                        onClick={() => selectUser(item)}
                      />
                    }
                    label={
                      <div style={{ paddingTop: 10, paddingBottom: 10 }}>
                        <Typography variant="h6"> {item?.first_name ? `${item.last_name}, ${item.first_name}` : item?.invitee_name}</Typography>
                        {item.current_positions?.map((position, index) => (
                          <Typography
                            key={index}
                            variant="caption"
                            className={classes.itemValue}
                          >
                            {position.hospital.label}
                          </Typography>
                        ))}

                      </div>
                    }
                  />
                </Box>
                <Divider></Divider>
              </Box>
            ))
          ) : (
            <Box>
              {performSearch && searchValue ? (
                <Box mb={2} className={classes.emptyView}>
                  <Typography className={classes.centerText} variant="caption">
                    <img
                      src={!isDarkTheme ? emptySaerch : emptySaerchDark}
                      alt="no data"
                    ></img>
                    <Box>
                      {' '}
                      {searchValue} does not match any name in your institution.
                    </Box>
                  </Typography>
                </Box>
              ) : (
                <Box mb={2} className={classes.emptyView}>
                  <Typography className={classes.centerText} variant="caption">
                    <img
                      src={!isDarkTheme ? emptySaerch : emptySaerchDark}
                      alt="no data"
                    ></img>
                    <Box> Start typing to get results!</Box>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      </Paper>
      <Box pt={3} className={classes.buttonsContainer}>
        <Button
          onClick={() => onSave()}
          variant="contained"
          color="secondary"
          className={classes.footerButton}
        >
          Add
        </Button>
      </Box>
    </div>
  );
};
export default withWidth()(SearchUsers);
